import { API_BASE_URL } from "@kupikupi/brand";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { type CaptchaTokenProvider, withCaptchaRetry } from "../captcha-retry";
import { searchProducts } from "../endpoints/product";
import { useBackendLang } from "../lang-context";
import type { ProductSearchPagedInput } from "../path-adapter";
import { withSearchTiming } from "../search-timing";

type UseProductSearchInput = Omit<ProductSearchPagedInput, "pageNo">;

interface CaptchaProviderOpts {
  /** Provider that resolves a Yandex SmartCaptcha token. When set and the
   * backend returns ApiError("4601"), the hook will solve the captcha and
   * retry the same request once with the X-Captcha-Token header.
   *
   * Declared `T | undefined` (not just `T?`) under exactOptionalPropertyTypes
   * so consumers can pass `useCaptchaTokenProvider()` directly without a
   * conditional spread — that hook returns `undefined` when the provider is
   * not mounted (server render / test renderer). */
  captchaTokenProvider?: CaptchaTokenProvider | undefined;
}

/** Infinite-scroll variant — for catalog page with "load more" UX */
export function useProductSearch(input: UseProductSearchInput, opts: CaptchaProviderOpts = {}) {
  const lang = useBackendLang();
  return useInfiniteQuery({
    // `lang` is the last key so a locale switch refetches translated titles;
    // SSR prefetch in catalog/page.tsx mirrors this shape (keep in lockstep).
    queryKey: [
      "product",
      "search",
      input.channel,
      input.keyword,
      input.categoryId,
      input.pageSize,
      input.sort,
      input.priceStart,
      input.priceEnd,
      lang,
    ],
    queryFn: ({ pageParam = 1 }) =>
      withSearchTiming(
        {
          kind: "search",
          channel: input.channel,
          keywordLen: input.keyword?.length ?? 0,
          pageNo: pageParam as number,
        },
        () =>
          withCaptchaRetry(
            (captchaToken) =>
              searchProducts(
                { baseUrl: API_BASE_URL },
                { ...input, pageNo: pageParam as number },
                captchaToken === undefined ? { lang } : { captchaToken, lang },
              ),
            opts.captchaTokenProvider,
          ),
      ),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (lastPage.current < lastPage.pages) return lastPage.current + 1;
      return undefined;
    },
    staleTime: 30 * 1000,
    enabled: !!input.keyword,
  });
}

/** Classic pagination variant — for catalog page with page-number UX */
export function useProductSearchPaged(
  input: ProductSearchPagedInput,
  opts: CaptchaProviderOpts = {},
) {
  const lang = useBackendLang();
  return useQuery({
    queryKey: [
      "product",
      "search",
      input.channel,
      input.keyword,
      input.categoryId,
      input.pageNo,
      input.pageSize,
      input.sort,
      input.priceStart,
      input.priceEnd,
      lang,
    ],
    queryFn: () =>
      withCaptchaRetry(
        (captchaToken) =>
          searchProducts(
            { baseUrl: API_BASE_URL },
            input,
            captchaToken === undefined ? { lang } : { captchaToken, lang },
          ),
        opts.captchaTokenProvider,
      ),
    staleTime: 30 * 1000,
    enabled: !!input.keyword,
  });
}
